import { isAxiosError } from 'axios'
import toast from 'react-hot-toast'
import { AppError, AppErrorCode, DEFAULT_ERROR_MESSAGES } from '../types/error.types'

type ToAppErrorOptions = {
  fallbackCode?: AppErrorCode
  fallbackMessage?: string
}

type BackendErrorBody = {
  code?: unknown
  message?: unknown
}

export function toAppError(error: unknown, options: ToAppErrorOptions = {}): AppError {
  const fallbackCode = options.fallbackCode ?? AppErrorCode.GENERIC_ERROR
  const fallbackMessage = options.fallbackMessage ?? DEFAULT_ERROR_MESSAGES[fallbackCode]

  if (isAppError(error)) {
    return error
  }

  if (!isAxiosError(error)) {
    return {
      code: fallbackCode,
      message: error instanceof Error ? error.message : fallbackMessage,
    }
  }

  const { status } = error.response ?? {}
  const requestId = error.response?.headers['x-amzn-requestid'] as string | undefined

  if (!error.response) {
    return {
      code: AppErrorCode.NETWORK_ERROR,
      message: DEFAULT_ERROR_MESSAGES[AppErrorCode.NETWORK_ERROR],
      requestId,
    }
  }

  const backendError = readBackendErrorBody(error.response.data, status)
  if (backendError) {
    return {
      code: backendError.code,
      message: backendError.message,
      status,
      requestId,
    }
  }

  if (status === 401) {
    return {
      code: AppErrorCode.UNAUTHORIZED,
      message: DEFAULT_ERROR_MESSAGES[AppErrorCode.UNAUTHORIZED],
      status,
      requestId,
    }
  }

  return {
    code: fallbackCode,
    message: fallbackMessage,
    status,
    requestId,
  }
}

export function showError(error: unknown, options: ToAppErrorOptions = {}) {
  const appError = toAppError(error, options)
  const suffix = appError.requestId ? ` (requestId: ${appError.requestId})` : ''
  toast.error(`${appError.message}${suffix}`)
  console.error(`[${appError.code}]`, error)
}

function isAppError(error: unknown): error is AppError {
  return (
    !isAxiosError(error) &&
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof (error as AppError).code === 'string' &&
    typeof (error as AppError).message === 'string'
  )
}

function readBackendErrorBody(data: unknown, status?: number): AppError | undefined {
  if (status === 424 && typeof data === 'string' && data.trim().length > 0) {
    return {
      code: 'FAILED_DEPENDENCY',
      message: data.trim(),
    }
  }

  if (typeof data !== 'object' || data === null) {
    return undefined
  }

  const { code, message } = data as BackendErrorBody
  if (typeof code !== 'string') {
    return undefined
  }

  return {
    code,
    message:
      typeof message === 'string' && message.trim().length > 0 ? message : getDefaultMessage(code),
  }
}

function getDefaultMessage(code: string): string {
  return isKnownAppErrorCode(code)
    ? DEFAULT_ERROR_MESSAGES[code]
    : DEFAULT_ERROR_MESSAGES[AppErrorCode.GENERIC_ERROR]
}

function isKnownAppErrorCode(code: string): code is AppErrorCode {
  return Object.values(AppErrorCode).includes(code as AppErrorCode)
}
